import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Shield, Clock, DollarSign, AlertTriangle, 
  TrendingUp, Calendar, Timer, Ban 
} from 'lucide-react';
import { toast } from 'sonner';

export function ResponsibleGaming() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [sessionStart] = useState(Date.now());
  const [sessionMinutes, setSessionMinutes] = useState(0);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['responsible-gaming', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('responsible_gaming')
        .select('*')
        .eq('user_id', user?.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: activity } = useQuery({
    queryKey: ['gaming-activity', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gaming_activity')
        .select('*')
        .eq('user_id', user?.id)
        .eq('activity_date', new Date().toISOString().split('T')[0])
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: any) => {
      const { error } = await supabase
        .from('responsible_gaming')
        .upsert({ user_id: user?.id, ...updates, updated_at: new Date().toISOString() });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['responsible-gaming'] });
      toast.success('Settings updated');
    },
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionMinutes(Math.floor((Date.now() - sessionStart) / 60000));
    }, 60000);
    return () => clearInterval(interval);
  }, [sessionStart]);

  const dailySpentPercent = activity?.total_wagered && settings?.daily_bet_limit
    ? (activity.total_wagered / settings.daily_bet_limit) * 100
    : 0;

  const sessionPercent = settings?.session_time_limit
    ? (sessionMinutes / settings.session_time_limit) * 100
    : 0;

  const isNearLimit = dailySpentPercent > 80 || sessionPercent > 80;

  return (
    <div className="space-y-6">
      {isNearLimit && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Approaching Limits</AlertTitle>
          <AlertDescription>
            You're approaching your daily betting or session time limit. Consider taking a break.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Today's Bets</span>
              <DollarSign className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-bold">${activity?.total_wagered?.toFixed(2) || '0.00'}</p>
            <Progress value={dailySpentPercent} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              of ${settings?.daily_bet_limit || 50} limit
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Session Time</span>
              <Clock className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-bold">{sessionMinutes} min</p>
            <Progress value={sessionPercent} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              of {settings?.session_time_limit || 120} min limit
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Win/Loss</span>
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <p className={`text-2xl font-bold ${(activity?.total_won || 0) - (activity?.total_wagered || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              ${((activity?.total_won || 0) - (activity?.total_wagered || 0)).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Today's net result</p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Bets Placed</span>
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-bold">{activity?.total_bets || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Today's activity</p>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Betting Limits
          </CardTitle>
          <CardDescription>Set your personal limits to stay in control</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Label>Daily Bet Limit ($)</Label>
              <Input
                type="number"
                defaultValue={settings?.daily_bet_limit || 50}
                onBlur={(e) => updateSettings.mutate({ daily_bet_limit: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-3">
              <Label>Weekly Deposit Limit ($)</Label>
              <Input
                type="number"
                defaultValue={settings?.weekly_deposit_limit || 500}
                onBlur={(e) => updateSettings.mutate({ weekly_deposit_limit: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-3">
              <Label>Monthly Deposit Limit ($)</Label>
              <Input
                type="number"
                defaultValue={settings?.monthly_deposit_limit || 2000}
                onBlur={(e) => updateSettings.mutate({ monthly_deposit_limit: parseFloat(e.target.value) })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Session Time Limit: {settings?.session_time_limit || 120} minutes</Label>
            <Slider
              defaultValue={[settings?.session_time_limit || 120]}
              max={480}
              min={30}
              step={30}
              onValueCommit={(value) => updateSettings.mutate({ session_time_limit: value[0] })}
            />
          </div>

          <div className="space-y-3">
            <Label>Reality Check Interval: {settings?.reality_check_interval || 60} minutes</Label>
            <Slider
              defaultValue={[settings?.reality_check_interval || 60]}
              max={120}
              min={15}
              step={15}
              onValueCommit={(value) => updateSettings.mutate({ reality_check_interval: value[0] })}
            />
            <p className="text-sm text-muted-foreground">
              Get reminders about your betting activity at regular intervals
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Timer className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">Limit Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Get alerts when approaching limits
                </p>
              </div>
            </div>
            <Switch
              checked={settings?.notifications_enabled ?? true}
              onCheckedChange={(checked) => updateSettings.mutate({ notifications_enabled: checked })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Ban className="h-5 w-5" />
            Self-Exclusion Options
          </CardTitle>
          <CardDescription>Take a break or exclude yourself from betting</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="h-auto py-4 flex-col">
              <Clock className="h-5 w-5 mb-2" />
              <span>24-Hour Cool Off</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col">
              <Calendar className="h-5 w-5 mb-2" />
              <span>7-Day Break</span>
            </Button>
            <Button variant="destructive" className="h-auto py-4 flex-col">
              <Ban className="h-5 w-5 mb-2" />
              <span>Self-Exclude (30 Days)</span>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Need help? Contact our support team or visit{' '}
            <a href="https://www.begambleaware.org" target="_blank" className="text-primary hover:underline">
              BeGambleAware.org
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
